import React from 'react';
import { Shield, MapPin, Cookie, FileText, ShieldCheck } from 'lucide-react';
import { CLINIC_CONFIG } from '../utils/whatsapp';
import { ClinicLogo } from './ClinicLogo';
import { SpecialistAvatar } from './SpecialistAvatar';
import { CertificationBadges } from './CertificationBadges';
import { useSite } from '../context/SiteContext';

interface FooterProps {
  onOpenAdminModal?: () => void;
  onNavigateToBlog?: () => void;
  onNavigateHome?: () => void;
}

export const Footer: React.FC<FooterProps> = ({
  onOpenAdminModal,
  onNavigateToBlog,
  onNavigateHome,
}) => {
  const { isApollon, isFemale, siteProfile } = useSite();

  const officialDomains = isApollon
    ? [siteProfile.domain || 'apollonguzellik.com.tr', 'apollonguzellik.com']
    : isFemale
    ? [siteProfile.domain || 'bursakadinlazer.com.tr', 'bursakadinlazer.com']
    : [siteProfile.domain || 'bursaerkeklazer.com.tr', 'bursaerkeklazer.com'];

  const popularAreas = isApollon
    ? [
        { label: 'Hydrafacial Medikal Cilt Bakımı', href: '#hizmetler' },
        { label: 'Medikal Botoks & Masseter', href: '#hizmetler' },
        { label: 'Dudak & Jawline Yüz Dolgusu', href: '#hizmetler' },
        { label: 'Gençlik Aşısı & Somon DNA', href: '#hizmetler' },
        { label: 'Kadın Safir Buz Lazer Kliniği', href: '#lazer-klinikleri' },
        { label: 'Erkek Safir Buz Lazer Kliniği', href: '#lazer-klinikleri' },
      ]
    : isFemale
    ? [
        { label: 'Tüm Bacak & Çilek Bacak Bakımı', href: '#bolgeler' },
        { label: 'Koltuk Altı Lazer Epilasyon', href: '#bolgeler' },
        { label: 'Genital & Bikini Bölgesi Hassas Lazer', href: '#bolgeler' },
        { label: 'Yüz & Favori & Bıyık Lazer', href: '#bolgeler' },
        { label: 'Kol & Göbek Çizgisi Lazeri', href: '#bolgeler' },
        { label: 'Full Vücut Kadın Konfor Paketi', href: '#bolgeler' },
      ]
    : [
        { label: 'Sakal Üstü & Yanak Lazer', href: '#bolgeler' },
        { label: 'Ense & Boyun Batık Tedavisi', href: '#bolgeler' },
        { label: 'Sırt & Omuz Lazer Epilasyon', href: '#bolgeler' },
        { label: 'Göğüs & Karın Seyreltme', href: '#bolgeler' },
        { label: 'Koltuk Altı Hijyen Lazeri', href: '#bolgeler' },
        { label: 'Full Vücut Erkek Paketi', href: '#bolgeler' },
      ];

  const regionalTags = isApollon
    ? [
        'Bursa Güzellik Merkezi',
        'Bursa Medikal Estetik',
        'Nilüfer FSM Cilt Bakımı',
        'Bursa Botoks & Dolgu',
        'Hydrafacial FSM',
        'Bursa Dudak Dolgusu',
        'Bursa Gençlik Aşısı',
        'Kadın & Erkek Buz Lazer',
        'FSM Bulvarı Medikal Estetik',
        'Bursa Estetik Fiyatları',
      ]
    : isFemale
    ? [
        'Bursa Kadın Lazer Epilasyon',
        'Nilüfer Kadın Epilasyon',
        'FSM Bulvarı Kadın Buz Lazer',
        'Özlüce Kadın Lazer',
        'Batık & Çilek Bacak Tedavisi',
        'Genital & Bikini Buz Lazer',
        'Tüm Bacak Kadın Epilasyon',
        'Yüz & Bıyık Hassas Lazer',
        'Bursa Kadın Lazer Fiyatları',
        'Kadın Estetisyen Buz Lazer',
      ]
    : [
        'Bursa Erkek Lazer Epilasyon',
        'Nilüfer Erkek Epilasyon',
        'FSM Bulvarı Buz Lazer',
        'Özlüce Erkek Lazer',
        'Sakal Üstü Çizgi Lazeri',
        'Erkek Sırt & Omuz Lazeri',
        'Göğüs Karın Lazer Epilasyon',
        'Ense Boyun Batık Tedavisi',
        'Bursa Erkek Lazer Fiyatları',
        'Doktor Kliniği Buz Lazer',
      ];

  const accentColor = isApollon ? 'text-amber-400' : isFemale ? 'text-rose-400' : 'text-amber-400';
  const hoverAccent = isApollon ? 'hover:text-amber-400' : isFemale ? 'hover:text-rose-400' : 'hover:text-amber-400';
  const badgeColor = isApollon ? 'text-amber-400' : isFemale ? 'text-rose-400' : 'text-amber-400';

  return (
    <footer className="bg-slate-900 border-t border-slate-800 pt-8 pb-24 lg:pb-12 text-slate-300 text-xs">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-8 lg:px-12 xl:px-16">
        
        {/* 1. Regulatory & Medical Device Certifications Bar (Top of Footer) */}
        <div className="pb-6 border-b border-slate-800/80 mb-8">
          <CertificationBadges variant="dark" showTitle={true} />
        </div>

        {/* 2. Main 4-Column Footer (Quick Links & Contact) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 pb-12 border-b border-slate-800">
          
          {/* Brand Info (4 Cols) */}
          <div className="lg:col-span-4 space-y-4">
            <ClinicLogo variant="full" size="md" isDark={true} />

            <p className="text-slate-300 leading-relaxed text-xs font-normal">
              {isApollon
                ? 'Bursa Nilüfer Fatih Sultan Mehmet (FSM) Bulvarı’nda hekim güvencesiyle medikal cilt bakımı, Hydrafacial, botoks, dolgu, gençlik aşısı ve kadın-erkek özel buz lazer epilasyon klinikleri.'
                : isFemale
                ? 'Bursa Nilüfer Fatih Sultan Mehmet (FSM) Bulvarı’nda doktor kliniği güvencesi ve uzman kadın estetisyenler eşliğinde kadınlara özel medikal buz lazer epilasyon hizmeti.'
                : 'Bursa Nilüfer Fatih Sultan Mehmet (FSM) Bulvarı’nda doktor kliniği güvencesiyle erkeklere özel medikal buz lazer epilasyon hizmeti.'}
            </p>

            <div className="space-y-2 text-slate-300">
              <div className="flex items-center gap-2">
                <MapPin className={`w-4 h-4 ${accentColor} shrink-0`} />
                <span>{CLINIC_CONFIG.address}</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Doktor Kliniği Standartlarında Güvenli Seans</span>
              </div>
            </div>

            {/* Domains ownership badge */}
            <div className="bg-slate-950 p-3.5 rounded-2xl border border-slate-800 space-y-1">
              <span className={`text-[11px] font-bold ${badgeColor} block`}>Resmi Web Alan Adlarımız:</span>
              <div className="flex flex-wrap gap-2 text-slate-300 font-mono text-[11px]">
                {officialDomains.map((dom) => (
                  <span key={dom}>&bull; {dom}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Navigation Links (2 Cols) */}
          <div className="lg:col-span-2 space-y-3">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">Hızlı Erişim</h4>
            <ul className="space-y-2">
              {isApollon ? (
                <>
                  <li><a href="#hizmetler" className={`${hoverAccent} transition-colors`}>Medikal Hizmetler</a></li>
                  <li><a href="#lazer-klinikleri" className={`${hoverAccent} transition-colors`}>Lazer Klinikleri</a></li>
                  <li><a href="#neden-biz" className={`${hoverAccent} transition-colors`}>Hekim & Klinik</a></li>
                  <li><a href="#fiyat-al" className={`${hoverAccent} transition-colors`}>Randevu & Bilgi</a></li>
                  <li><a href="#blog" className={`${hoverAccent} transition-colors`}>SEO Blog & Rehber</a></li>
                  <li><a href="#sss" className={`${hoverAccent} transition-colors`}>Sıkça Sorulanlar</a></li>
                  <li><a href="#iletisim" className={`${hoverAccent} transition-colors`}>Konum & Ulaşım</a></li>
                </>
              ) : (
                <>
                  <li><a href="#bolgeler" className={`${hoverAccent} transition-colors`}>Uygulama Bölgeleri</a></li>
                  <li><a href="#fiyat-hesapla" className={`${hoverAccent} transition-colors`}>Bölge & Randevu Planı</a></li>
                  <li><a href="#doktor-guvencesi" className={`${hoverAccent} transition-colors`}>Doktor Güvencesi</a></li>
                  <li><a href="#teknoloji" className={`${hoverAccent} transition-colors`}>Buz Lazer Teknolojisi</a></li>
                  <li><a href="#blog" className={`${hoverAccent} transition-colors`}>SEO Blog & Rehber</a></li>
                  <li><a href="#sss" className={`${hoverAccent} transition-colors`}>Sıkça Sorulanlar</a></li>
                  <li><a href="#iletisim" className={`${hoverAccent} transition-colors`}>Konum & Ulaşım</a></li>
                </>
              )}
            </ul>
          </div>

          {/* Treatment Areas (3 Cols) */}
          <div className="lg:col-span-3 space-y-3">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">
              {isApollon ? 'Popüler Hizmetlerimiz' : isFemale ? 'Popüler Kadın Bölgeleri' : 'Popüler Erkek Bölgeleri'}
            </h4>
            <ul className="space-y-2">
              {popularAreas.map((area, idx) => (
                <li key={idx}>
                  <a href={area.href} className={`${hoverAccent} transition-colors`}>
                    {area.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Direct & Hours (3 Cols) */}
          <div className="lg:col-span-3 space-y-3">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">İletişim & Randevu</h4>
            <p className="text-slate-300 text-xs font-normal">
              {isApollon
                ? 'FSM Bulvarı kliniğimizde ücretsiz cilt analizi, hekim danışmanlığı ve seans randevuları için uzmanımızla hemen mesajlaşabilirsiniz.'
                : isFemale
                ? 'FSM Bulvarı kliniğimizde ön görüşme, cilt ve kıl analizi için uzmanımızla hemen mesajlaşabilirsiniz.'
                : 'FSM Bulvarı kliniğimizde ön görüşme ve ücretsiz kıl analizi için uzmanımızla hemen mesajlaşabilirsiniz.'}
            </p>
            
            <div className="space-y-2.5 pt-1">
              <button
                type="button"
                onClick={() => {
                  if (typeof window !== 'undefined' && window.openLiveChat) {
                    window.openLiveChat();
                  } else if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('open-live-chat'));
                  }
                }}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs shadow-sm border border-slate-700 ${
                  isFemale ? 'hover:border-rose-500/40' : 'hover:border-amber-500/40'
                } transition-all hover:scale-[1.01] group cursor-pointer`}
              >
                <SpecialistAvatar size="xs" showOnlineStatus={true} bordered={false} />
                <span className={`${isFemale ? 'group-hover:text-rose-300' : 'group-hover:text-amber-300'} transition-colors`}>
                  Canlı Destek Başlat
                </span>
              </button>

              <div className="text-slate-200 font-bold pt-1">
                Klinik Tel: <a href={`tel:${CLINIC_CONFIG.landlineRaw}`} className={`${hoverAccent} font-extrabold`}>{CLINIC_CONFIG.landlineDisplay}</a>
              </div>
              <div className="text-slate-300 text-xs flex items-center gap-1.5">
                <span>Canlı Destek:</span>
                <span className="text-emerald-400 font-bold flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Çevrim İçi
                </span>
              </div>
              <div className={`${accentColor} font-semibold text-[11px]`}>
                Hizmet: Yalnızca Randevu İle
              </div>
            </div>
          </div>

        </div>

        {/* SEO & Local Medical Service Matrix (Modern Semantic SEO) */}
        <div className="py-8 border-b border-slate-800/80 space-y-5 text-xs text-slate-300">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Lokasyonlar */}
            <div className="space-y-2">
              <span className={`text-[11px] font-bold ${accentColor} uppercase tracking-wider block`}>
                Bursa Hizmet Lokasyonları
              </span>
              <p className="text-[11px] text-slate-300 leading-relaxed">
                {isApollon ? (
                  <>
                    Nilüfer FSM Bulvarı merkezli <strong className="text-slate-200">Apollon Güzellik Merkezi</strong>; <strong className="text-slate-200">Özlüce, Görükle, Osmangazi, Yıldırım, Mudanya, Balat, İhsaniye ve Ataevler</strong> bölgelerinden gelen danışanlarımıza medikal estetik, cilt bakımı ve lazer uygulamalarında hekim güvencesiyle randevulu hizmet sunmaktadır.
                  </>
                ) : isFemale ? (
                  <>
                    Nilüfer FSM Bulvarı merkezli kliniğimiz; <strong className="text-slate-200">Özlüce, Görükle, Osmangazi, Yıldırım, Mudanya, Balat, İhsaniye ve Ataevler</strong> bölgelerinden gelen kadın danışanlarımıza uzman kadın estetisyen kadrosu, kolay ulaşım ve otopark avantajıyla randevulu hizmet sunmaktadır.
                  </>
                ) : (
                  <>
                    Nilüfer FSM Bulvarı merkezli kliniğimiz; <strong className="text-slate-200">Özlüce, Görükle, Osmangazi, Yıldırım, Mudanya, Balat, İhsaniye ve Ataevler</strong> bölgelerinden gelen erkek danışanlarımıza kolay ulaşım ve otopark avantajıyla randevulu hizmet sunmaktadır.
                  </>
                )}
              </p>
            </div>

            {/* Tedavi Protokolleri */}
            <div className="space-y-2">
              <span className={`text-[11px] font-bold ${accentColor} uppercase tracking-wider block`}>
                {isApollon ? 'Apollon Medikal & Estetik Protokolleri' : isFemale ? 'Kadın Medikal Lazer Protokolleri' : 'Erkek Medikal Lazer Protokolleri'}
              </span>
              <p className="text-[11px] text-slate-300 leading-relaxed">
                {isApollon ? (
                  <>
                    FDA onaylı orijinal ürünlerle <strong className="text-slate-200">Hydrafacial Medikal Cilt Bakımı</strong>, <strong className="text-slate-200">Botoks & Masseter</strong>, <strong className="text-slate-200">Dudak & Yüz Dolgusu</strong>, <strong className="text-slate-200">Gençlik Aşısı & Somon DNA</strong> ve -5°C safir soğutmalı kadın/erkek buz lazer epilasyon seansları uygulanmaktadır.
                  </>
                ) : isFemale ? (
                  <>
                    Kadın cilt ve kıl yapısına özel, batık ve çilek bacak görünümünü gideren <strong className="text-slate-200">Buz Başlıklı Hibrit Diode Lazer</strong> teknolojisi ile tüm bacak, genital, koltuk altı, kol ve yüz seanslarında acısız, pürüzsüz ve kalıcı sonuçlar hedeflenir.
                  </>
                ) : (
                  <>
                    Erkek kıl kökü anatomisine uygun <strong className="text-slate-200">Buz Başlıklı Hibrit Diode Lazer</strong> teknolojisi ile sırt, omuz, göğüs, sakal üstü yanak çizgisi, ense, boyun batıkları ve tüm vücut seanslarında acısız ve kalıcı sonuçlar hedeflenir.
                  </>
                )}
              </p>
            </div>

            {/* Klinik Güvencesi & Hekim Denetimi */}
            <div className="space-y-2">
              <span className={`text-[11px] font-bold ${accentColor} uppercase tracking-wider block`}>
                Hekim Denetimli Güvenilir Seans
              </span>
              <p className="text-[11px] text-slate-300 leading-relaxed">
                T.C. Sağlık Bakanlığı mevzuatına uygun, FDA ve CE medikal sertifikalı cihaz altyapısıyla steril klinik ortamında kişiye özel doz ayarlaması ve ücretsiz analiz gerçekleştirilmektedir.
              </p>
            </div>
          </div>

          {/* Quick Filter Search Tags */}
          <div className="pt-2 flex flex-wrap items-center gap-1.5 text-[10px]">
            <span className="text-slate-300 font-semibold mr-1">Bursa Bölgesel Rehber:</span>
            {regionalTags.map((tag, idx) => (
              <a
                key={idx}
                href={isApollon ? '#hizmetler' : '#fiyat-hesapla'}
                className={`px-2.5 py-1 rounded-lg bg-slate-950 hover:bg-slate-800 text-slate-300 ${
                  isFemale ? 'hover:text-rose-300' : 'hover:text-amber-300'
                } border border-slate-800/80 transition-colors`}
              >
                {tag}
              </a>
            ))}
          </div>
        </div>

        {/* Bottom Bar: Copyright, Legal Links & Medical Disclaimer */}
        <div className="pt-8 border-t border-slate-800/80 space-y-6">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            
            {/* Trinovex Holding & Brand Copyright */}
            <div className="flex flex-col md:flex-row items-center gap-6">
              <a
                href="https://trinovex.com"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-80 hover:opacity-100 transition-all hover:-translate-y-0.5 shrink-0"
                title="Trinovex Holding"
              >
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 240 240"
                  fill="currentColor"
                  className="text-slate-300 hover:text-slate-100 transition-colors"
                >
                  <path d="M 10 60 L 120 90 L 230 60 L 220 30 L 120 60 L 20 30 Z" />
                  <path d="M 105 110 L 135 110 L 135 190 L 120 210 L 105 190 Z" />
                </svg>
              </a>
              <div className="font-bold text-[11px] text-slate-300 leading-relaxed uppercase tracking-wider text-center md:text-left">
                © 2016 — 2026{' '}
                <span
                  onClick={onNavigateHome}
                  className={`text-slate-100 ${hoverAccent} transition-colors cursor-pointer font-extrabold`}
                  title={siteProfile.siteName}
                >
                  {siteProfile.siteName || (isApollon ? 'Apollon Güzellik Merkezi' : isFemale ? 'Bursa Kadın Lazer Merkezi' : 'Bursa Erkek Lazer Merkezi')}
                </span>{' '}
                · TÜM HAKLARI SAKLIDIR
              </div>
            </div>

            {/* Legal / KVKK / Cookie Action Links */}
            <div className="flex items-center gap-4 flex-wrap justify-center text-[11px]">
              <button
                type="button"
                onClick={() => {
                  if (typeof window !== 'undefined' && (window as any).openCookieSettings) {
                    (window as any).openCookieSettings('preferences');
                  } else if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('open-cookie-settings', { detail: { tab: 'preferences' } }));
                  }
                }}
                className={`hover:${accentColor} text-slate-300 transition-colors inline-flex items-center gap-1.5 cursor-pointer`}
              >
                <Cookie className={`w-3.5 h-3.5 ${isFemale ? 'text-rose-400' : 'text-amber-400'}`} />
                <span>Çerez Tercihleri</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  if (typeof window !== 'undefined' && (window as any).openCookieSettings) {
                    (window as any).openCookieSettings('kvkk');
                  } else if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('open-cookie-settings', { detail: { tab: 'kvkk' } }));
                  }
                }}
                className={`hover:${accentColor} text-slate-300 transition-colors inline-flex items-center gap-1.5 cursor-pointer`}
              >
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                <span>6698 KVKK Aydınlatma</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  if (typeof window !== 'undefined' && (window as any).openCookieSettings) {
                    (window as any).openCookieSettings('terms');
                  } else if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('open-cookie-settings', { detail: { tab: 'terms' } }));
                  }
                }}
                className={`hover:${accentColor} text-slate-400 transition-colors inline-flex items-center gap-1.5 cursor-pointer`}
              >
                <FileText className="w-3.5 h-3.5 text-cyan-500" />
                <span>Kullanım Koşulları</span>
              </button>
            </div>

          </div>

          {/* Medical Disclaimer Note */}
          <div className="pt-2 text-center text-slate-500 text-[10px] sm:text-[11px] border-t border-slate-800/40">
            * Yasal Bilgilendirme: Sitemizde yer alan tüm bilgiler genel bilgilendirme amaçlıdır ve hekim muayenesi yerine geçmez.
          </div>
        </div>

      </div>
    </footer>
  );
};

